import { DEFAULT_TRUSS_DESIGN_DRAWING_OPTIONS, DownloadRequest } from '@paragon/contract-library';

import type { RootState } from '../../store';
import {
  downloadAnalysisReportPdf,
  downloadConvertedFileType,
  downloadLayoutPdf,
  downloadReport,
  downloadShopDrawings,
  downloadTrussDesignDrawing,
  generateDxfForActiveComponentDesign,
} from '../../store/actions/downloadActions';
import { downloadLayoutAsDxf } from '../../store/actions/layout/layoutActions';
import { getDownloadRequestForBulk, getDownloadRequestForSingle } from '../../store/selectors/downloadSelectors';
import { getActiveLevelOrNull } from '../../store/selectors/layoutDataSelectors';
import { findTrussByName, openTruss, requireProject, requireString, waitForAppToSettle } from './helpers';
import { defineTool, ToolError } from './types';

export const DOWNLOADS = [
  'shop drawings',
  'truss design drawings',
  'material report PDF',
  'material report CSV',
  'lumber pick list',
  'plate pick list',
  'component report',
  'analysis report',
  'layout PDF',
  'layout DXF',
  'truss DXF',
  'repair truss design drawing',
  'tre',
  'tps',
  'pcd',
  'bvn',
  'omn',
  'ifc',
] as const;

type Download = (typeof DOWNLOADS)[number];

const CONVERTED_FILE_TYPES = ['tre', 'tps', 'pcd', 'bvn', 'omn', 'ifc'] as const;

/**
 * The request the Downloads dialog builds for the whole project, narrowed to the named trusses when given.
 */
export function buildDownloadRequest(
  state: RootState,
  trussNames: readonly string[] | undefined,
  getBulkRequest: (state: RootState) => DownloadRequest | null = getDownloadRequestForBulk,
): DownloadRequest {
  const bulkRequest = getBulkRequest(state);
  if (bulkRequest == null) throw new ToolError('There is nothing to download here.');
  if (trussNames == null) return bulkRequest;

  const guidToQuantity = Object.fromEntries(
    trussNames.map((name) => {
      const { guid } = findTrussByName(state, name);
      return [guid, bulkRequest.guidToQuantity[guid] ?? 1];
    }),
  );
  return { ...bulkRequest, guidToQuantity };
}

/**
 * The request the Downloads dialog builds for the open truss.
 */
function singleRequest(state: RootState): DownloadRequest {
  const request = getDownloadRequestForSingle(state);
  if (request == null) throw new ToolError('There is nothing to download here.');
  return request;
}

export const outputTools = [
  defineTool<{ download: Download; trusses?: string[] }>({
    name: 'download',
    description:
      'Starts one of the app’s downloads, with the same file names and settings as its Downloads dialog. Defaults to ' +
      'every truss in the project; give trusses to narrow it. The analysis report is for one truss; the layout PDF is ' +
      'for the active level, and the layout DXF too. Truss DXFs and repair drawings are for one truss. The browser ' +
      'saves the file.',
    inputSchema: {
      type: 'object',
      properties: {
        download: { type: 'string', enum: DOWNLOADS },
        trusses: { type: 'array', items: { type: 'string' } },
      },
      required: ['download'],
    },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      requireProject(getState());
      const download = requireString(input, 'download') as Download;
      if (!DOWNLOADS.includes(download)) throw new ToolError(`Unknown download "${download}".`);

      const request = () => buildDownloadRequest(getState(), input.trusses);

      switch (download) {
        case 'shop drawings':
          await dispatch(downloadShopDrawings(request(), 'custom', true));
          break;
        case 'truss design drawings':
          if (input.trusses != null && input.trusses.length === 1) {
            await openTruss(dispatch, getState, input.trusses[0]);
            await dispatch(
              downloadTrussDesignDrawing(singleRequest(getState()), 'single', DEFAULT_TRUSS_DESIGN_DRAWING_OPTIONS),
            );
          } else if (input.trusses == null) {
            await dispatch(downloadTrussDesignDrawing(request(), 'bulk', DEFAULT_TRUSS_DESIGN_DRAWING_OPTIONS));
          } else {
            throw new ToolError('Truss design drawings download for one truss or for the whole project.');
          }
          break;
        case 'material report PDF':
          await dispatch(downloadReport(request(), 'Material Report PDF', true));
          break;
        case 'material report CSV':
          await dispatch(downloadReport(request(), 'Material Report CSV', true));
          break;
        case 'lumber pick list':
          await dispatch(downloadReport(request(), 'Lumber Pick List', true));
          break;
        case 'plate pick list':
          await dispatch(downloadReport(request(), 'Plate Pick List', true));
          break;
        case 'component report':
          await dispatch(downloadReport(request(), 'Component Report', true));
          break;
        case 'analysis report': {
          if (input.trusses?.length !== 1) throw new ToolError('Name the one truss for the analysis report.');
          await openTruss(dispatch, getState, input.trusses[0]);
          await dispatch(downloadAnalysisReportPdf());
          break;
        }
        case 'layout DXF': {
          const level = getActiveLevelOrNull(getState());
          await dispatch(downloadLayoutAsDxf(level?.guid, level?.name)());
          break;
        }
        case 'truss DXF':
          if (input.trusses?.length !== 1) throw new ToolError('Name the one truss for its DXF.');
          await openTruss(dispatch, getState, input.trusses[0]);
          await dispatch(generateDxfForActiveComponentDesign());
          break;
        case 'repair truss design drawing':
          if (input.trusses?.length !== 1) throw new ToolError('Name the one truss for its repair drawing.');
          await openTruss(dispatch, getState, input.trusses[0]);
          await dispatch(
            downloadTrussDesignDrawing(singleRequest(getState()), 'single', {
              ...DEFAULT_TRUSS_DESIGN_DRAWING_OPTIONS,
              useRepairTemplate: true,
            }),
          );
          break;
        case 'layout PDF': {
          const level = getActiveLevelOrNull(getState());
          await dispatch(downloadLayoutPdf(level?.guid, undefined, level?.name));
          break;
        }
        default:
          if (!(CONVERTED_FILE_TYPES as readonly string[]).includes(download))
            throw new ToolError(`Unknown download "${download}".`);
          await dispatch(downloadConvertedFileType(request(), download as (typeof CONVERTED_FILE_TYPES)[number], true));
      }

      await waitForAppToSettle(getState);
      return {
        summary: {
          started: download,
          trusses: input.trusses ?? 'all',
          note: 'The browser saves the file. Errors, if any, show in the app’s notifications.',
        },
      };
    },
  }),
];
